// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LinkPresentationProps, StyledClickable, StyledTypography } from "@insite/mobius/Link";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { getProp, resolveColor } from "@insite/mobius/utilities";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import IconTextLinkArrowRightInactive from "@tps/XDL/Icons/IconTextLinkArrowRightInactive";
import { avoidBreakInsidePrintElement, styledScrollbar } from "@tps/XDL/Utilities";
import color, { hexToRgb } from "@tps/XDL/Utilities/color";
import { iconButtonCommonStyles } from "@tps/XDL/Utilities/links";
import { mergeWithCss } from "@tps/XDL/Utilities/mergeWithCss";
import styled, { css } from "styled-components";

export const styledLinksMenu = (width: number) => {
    const xColor = {
        background: {
            hover: "common.ui4",
        },
        text: {
            default: "text",
            hover: "text",
        },
    };

    return ({ theme }: any) => css`
        ${getProp("theme.tooltip.defaultProps.cssOverrides.tooltipContainer")}

        padding: 5px 0 0;
        width: ${width}px;
        display: flex;
        flex-direction: column;
        max-height: 190px;
        border-radius: 4px;
        ${StyledClickable} {
            display: flex;
            width: 100%;
            ${/* sc-selector */ StyledTypography} {
                font-size: 13px;
                padding: 10px 15px;
                color: ${resolveColor(xColor.text.default, theme)};
                font-weight: normal;
                text-transform: none;
                letter-spacing: 0;
                width: 100%;
            }
            &:not(:disabled):not([disabled]):hover ${/* sc-selector */ StyledTypography} {
                background: ${resolveColor(xColor.background.hover, theme)};
                color: ${resolveColor(xColor.text.hover, theme)};
            }
        }

        ${styledScrollbar()}
    `;
};

/**
 * Hide input increment/decrement arrows from browser when textField type = number
 * */
export const hideInputNumberArrows = css`
    // Hide Arrows From Input Number
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Firefox */
    &[type="number"] {
        appearance: textfield;
        -moz-appearance: textfield;
    }
`;

export const companyLogoItem: InjectableCss = {
    css: css`
        display: none;

        @media print {
            display: block;
            padding: 0;
            margin: 0 15px;
            width: 100%;
        }
    `,
};

export const createLinkIconStyles = (
    icon: React.ComponentType | string,
    linkStyles?: LinkPresentationProps,
    typographyStyles?: StyledProp,
    iconStyles?: StyledProp,
    iconPosition: "left" | "right" = "left",
) => {
    const marginPosition = (linkStyles?.icon?.position || iconPosition) === "right" ? "margin-left" : "margin-right";
    return {
        ...mergeWithCss(
            {
                typographyProps: {
                    size: 12,
                    lineHeight: "17px",
                    weight: "bold",
                    css: css`
                        letter-spacing: 0;
                        ${typographyStyles}
                    `,
                },
                icon: {
                    iconProps: {
                        src: icon,
                        size: 12,
                        css: css`
                            ${marginPosition}: 8px;
                            opacity: 0.66;
                            ${iconStyles}
                        `,
                    },
                    position: linkStyles?.icon?.position || iconPosition,
                },
            },
            linkStyles,
        ),
    } as LinkPresentationProps;
};

export const circleListStyle = css`
    &:before {
        content: "";
        display: inline-block;
        width: 8px;
        min-width: 8px;
        max-width: 8px;
        height: 8px;
        min-height: 8px;
        max-height: 8px;
        border-radius: 50%;
        background-color: ${color("common.ui8")};
        margin-top: 8px;
        margin-right: 8px;
    }
`;

export const pageTitleStyle: TypographyProps = {
    variant: "h1",
    size: 32,
    weight: 900,
    css: css`
        line-height: 40px;
        margin-bottom: 20px;
        display: block;
    `,
};

export const generateButtonLinkStyle = (iconSrc: React.ComponentType | string) => {
    return {
        ...mergeWithCss(
            iconButtonCommonStyles(iconSrc, 12, {
                typographyProps: {
                    size: 12,
                    weight: "bold",
                },
            }),
            {
                css: css`
                    margin: 0 10px;
                    display: inline-block;
                `,
            },
        ),
    };
};

interface ModalButtonsActionStyles {
    buttonsGridItem?: GridItemProps;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const modalButtonsActionStyles: ModalButtonsActionStyles = {
    buttonsGridItem: {
        width: 12,
        css: css`
            &&& {
                display: flex;
                justify-content: space-between;
                flex-wrap: wrap;
                row-gap: 15px;
                padding-top: 16px;
                margin-top: 12px;
                position: relative;
            }

            &::before {
                content: "";
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                width: 100%;
                height: 1px;
                border-top: 1px solid ${color("common.border")};
                margin: 0 auto;
            }
        `,
    },
    cancelButton: {
        variant: "secondary",
        css: css`
            margin-right: 10px;
        `,
    },
};

// This Wrapper used For total display ( order total, cart total, ...)
export const StyledBox = styled(StyledWrapper)`
    width: 100%;
    display: flex;
    padding: 13px;
    border: 1px solid ${color("common.border")};
    background-color: ${color("common.white")};
    @media print {
        max-width: 400px;
    }
    ${avoidBreakInsidePrintElement}
`;

interface SharedRecentWidgetsStyles {
    container?: InjectableCss;
    headingWrapper?: InjectableCss;
    widgetTitle?: TypographyProps;
    viewAllLink?: LinkPresentationProps;
    noResult?: InjectableCss;
}

export const sharedRecentWidgetsStyles: SharedRecentWidgetsStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    headingWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 12px;
        `,
    },
    widgetTitle: {
        variant: "h5",
        as: "h4",
        color: "common.ui6",
        css: css`
            margin-bottom: 0;
        `,
    },
    viewAllLink: {
        ...createLinkIconStyles(IconTextLinkArrowRightInactive, {
            typographyProps: {
                size: 12,
                weight: "bold",
                css: css`
                    letter-spacing: 0;
                    line-height: 17px;
                `,
            },
            icon: {
                position: "right",
            },
        }),
    },
    noResult: {
        css: css`
            max-width: 100%;
            margin-bottom: 0;
        `,
    },
};

export const fadeOutLeftStyles = css`
    &::before {
        content: "";
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 20px;
        pointer-events: none;
        background: linear-gradient(90deg, ${color("common.ui1")} 0%, ${color("common.ui1", 0)} 100%);
        z-index: 2;
    }
`;

export const fadeOutRightStyles = css`
    &::after {
        content: "";
        position: absolute;
        right: 0;
        top: 0;
        bottom: 0;
        width: 20px;
        pointer-events: none;
        background: linear-gradient(90deg, ${color("common.ui1", 0)} 0%, ${color("common.ui1")} 100%);
        z-index: 2;
    }
`;

export const createFadeOutEffect = (
    width = 100,
    direction: "left" | "right",
    colorCode = "common.white",
    fromColorPickerField = false,
) => {
    const angel = direction === "left" ? `-90deg` : `90deg`;
    let fullColor = "";
    let transparentColor = "";

    if (fromColorPickerField) {
        transparentColor = hexToRgb(colorCode || "#FFFFFF", 0);
        fullColor = hexToRgb(colorCode, 1);
    } else {
        transparentColor = `${color(`${colorCode}`, 0)}`;
        fullColor = `${color(`${colorCode}`)}`;
    }
    return css`
        content: "";
        position: absolute;
        top: 0;
        bottom: 0;
        ${direction}: 0;
        width: ${width}px;
        pointer-events: none;
        background: linear-gradient(${angel}, ${transparentColor} 0%, ${fullColor} 100%);
        z-index: 2;
    `;
};

export const getSharedAddressInfoDisplayStyles = (variant?: "small") => {
    const addressInfoTypographyStyles = {
        as: "p",
        size: variant === "small" ? 12 : 14,
        lineHeight: variant === "small" ? "17px" : "22px",
    } as TypographyPresentationProps;

    return {
        customerNameText: { ...addressInfoTypographyStyles },
        attentionText: { ...addressInfoTypographyStyles },
        companyNameText: { ...addressInfoTypographyStyles },
        address1Text: {
            ...addressInfoTypographyStyles,
            css:
                variant === "small"
                    ? css`
                          margin-top: 8px;
                      `
                    : css`
                          margin-top: 15px;
                      `,
        },
        address2Text: { ...addressInfoTypographyStyles },
        address3Text: { ...addressInfoTypographyStyles },
        address4Text: { ...addressInfoTypographyStyles },
        cityStatePostalCodeText: { ...addressInfoTypographyStyles },
        countryText: { ...addressInfoTypographyStyles },
        phoneText: { ...addressInfoTypographyStyles },
        emailText: { ...addressInfoTypographyStyles },
        faxText: { ...addressInfoTypographyStyles },
    } as AddressInfoDisplayStyles;
};
